import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChatContext } from '../contexts/ChatContext';
import CreateGroupModal from './CreateGroupModal';
import GroupSettingsModal from './GroupSettingsModal';
import api from '../services/api';
import './ChatPanel.css';

// Helper to parse @mentions in message text
const parseMentions = (text, members, currentUserId) => {
  if (!text) return [{ type: 'text', content: '' }];

  const mentionRegex = /@(\w+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }

    // Check if mentioned user exists in members
    const mentionedUser = members.find(
      m => m.user_name?.toLowerCase() === match[1].toLowerCase() ||
           `${m.first_name}${m.last_name}`.toLowerCase().replace(/\s+/g, '') === match[1].toLowerCase()
    );

    if (mentionedUser) {
      const isSelf = mentionedUser.user_id === currentUserId;
      parts.push({
        type: 'mention',
        content: match[0],
        userId: mentionedUser.user_id,
        isSelf
      });
    } else {
      parts.push({ type: 'text', content: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
};

// Helper to get sender color class
const getSenderColorClass = (senderId, members) => {
  const index = members.findIndex(m => m.user_id === senderId);
  return `sender-${(Math.abs(index) % 5) + 1}`;
};

const ChatPanel = () => {
  const {
    isPanelOpen,
    users,
    selectedUser,
    messages,
    loading,
    error,
    currentUser,
    autoOpenEnabled,
    unreadPerUser,
    // Group state
    activeTab,
    groups,
    selectedGroup,
    groupMessages,
    groupMembers,
    unreadPerGroup,
    groupUnreadCount,
    showCreateGroupModal,
    showGroupSettings,
    // DM actions
    closePanel,
    loadUsers,
    selectUser,
    sendMessage,
    toggleAutoOpen,
    // Group actions
    switchTab,
    loadGroups,
    selectGroup,
    sendGroupMessage,
    openCreateGroupModal,
    closeCreateGroupModal,
    openGroupSettings,
    closeGroupSettings,
  } = useChatContext();

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Room sidebar tabs state
  const [roomTab, setRoomTab] = useState('chat');
  const [roomRecords, setRoomRecords] = useState([]);
  const [roomRecordsLoading, setRoomRecordsLoading] = useState(false);

  const ROOM_TABS = [
    { key: 'chat', label: 'Chat' },
    { key: 'AOS_Products', label: 'Products' },
    { key: 'AOS_Invoices', label: 'Invoices' },
    { key: 'AOS_Quotes', label: 'Proposals' },
    { key: 'jcrm_PurchaseOrders', label: 'Purchase Orders' },
    { key: 'Tasks', label: 'Tasks' },
  ];

  const fetchRoomRecords = useCallback(async (module) => {
    if (module === 'chat') return;
    setRoomRecordsLoading(true);
    try {
      const res = await api.getRecords(module, { pageSize: 20, sort: '-date_modified' });
      const records = res?.data?.data || res?.data || [];
      setRoomRecords(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error(`Failed to fetch ${module} records:`, err);
      setRoomRecords([]);
    } finally {
      setRoomRecordsLoading(false);
    }
  }, []);

  // Reset room tab when leaving a conversation
  useEffect(() => {
    if (!selectedGroup) {
      setRoomTab('chat');
      setRoomRecords([]);
    }
  }, [selectedGroup]);

  // Fetch records when room tab changes
  useEffect(() => {
    if (selectedGroup && roomTab !== 'chat') {
      fetchRoomRecords(roomTab);
    }
  }, [roomTab, selectedGroup, fetchRoomRecords]);

  useEffect(() => {
    if (isPanelOpen) {
      if (activeTab === 'dms') {
        loadUsers();
      } else {
        loadGroups();
      }
    }
  }, [isPanelOpen, activeTab, loadUsers, loadGroups]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, groupMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      if (selectedGroup) {
        await sendGroupMessage(newMessage);
      } else {
        await sendMessage(newMessage);
      }
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (selectedGroup) {
      selectGroup(null);
    } else if (selectedUser) {
      selectUser(null);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Calculate total unread for DMs
  const dmUnreadTotal = Object.values(unreadPerUser).reduce((sum, count) => sum + count, 0);

  if (!isPanelOpen) return null;

  const isInConversation = selectedUser || selectedGroup;

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <h3 className="chat-panel-title">
          {selectedUser ? (
            <>
              <button
                className="chat-back-btn"
                onClick={handleBack}
                title="Back to users"
              >
                ←
              </button>
              <span>{selectedUser.name}</span>
            </>
          ) : selectedGroup ? (
            <>
              <button
                className="chat-back-btn"
                onClick={handleBack}
                title="Back to groups"
              >
                ←
              </button>
              <span>{selectedGroup.name}</span>
              <button
                className="chat-settings-btn"
                onClick={openGroupSettings}
                title="Group settings"
              >
                ⚙
              </button>
            </>
          ) : (
            'Team Chat'
          )}
        </h3>
        <button className="chat-close-btn" onClick={closePanel} title="Close">
          ×
        </button>
      </div>

      {/* Tab Bar - only show when not in a conversation */}
      {!isInConversation && (
        <div className="chat-tab-bar">
          <button
            className={`chat-tab ${activeTab === 'dms' ? 'active' : ''}`}
            onClick={() => switchTab('dms')}
          >
            DMs
            {dmUnreadTotal > 0 && (
              <span className="chat-tab-badge">
                {dmUnreadTotal > 99 ? '99+' : dmUnreadTotal}
              </span>
            )}
          </button>
          <button
            className={`chat-tab ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => switchTab('groups')}
          >
            Groups
            {groupUnreadCount > 0 && (
              <span className="chat-tab-badge">
                {groupUnreadCount > 99 ? '99+' : groupUnreadCount}
              </span>
            )}
          </button>
        </div>
      )}

      <div className="chat-panel-body">
        {loading && (
          <div className="chat-loading">Loading...</div>
        )}

        {error && (
          <div className="chat-error">{error}</div>
        )}

        {/* DM User List */}
        {!loading && !isInConversation && activeTab === 'dms' && (
          <>
            {/* Auto-open toggle */}
            <div className="chat-auto-open-toggle">
              <span className="chat-auto-open-label">Auto-open on new message</span>
              <button
                className={`chat-toggle-switch ${autoOpenEnabled ? 'enabled' : 'disabled'}`}
                onClick={toggleAutoOpen}
                title={autoOpenEnabled ? 'Disable auto-open' : 'Enable auto-open'}
                aria-pressed={autoOpenEnabled}
              >
                <span className="chat-toggle-slider"></span>
              </button>
            </div>

            <div className="chat-contacts-list">
              {users.length === 0 ? (
                <div className="chat-no-contacts">
                  <p>No other users available.</p>
                  <p className="chat-hint">Add more users in SuiteCRM to start chatting.</p>
                </div>
              ) : (
                users.map((user) => {
                  const userUnread = unreadPerUser[user.id] || 0;
                  return (
                    <button
                      key={user.id}
                      className="chat-contact-item"
                      onClick={() => selectUser(user)}
                    >
                      <div className="chat-contact-avatar">
                        {user.name?.charAt(0) || user.username?.charAt(0) || '?'}
                        {userUnread > 0 && (
                          <span className="chat-user-unread-badge">
                            {userUnread > 99 ? '99+' : userUnread}
                          </span>
                        )}
                      </div>
                      <div className="chat-contact-info">
                        <span className="chat-contact-name">{user.name || user.username}</span>
                        <span className="chat-contact-email">{user.email || `@${user.username}`}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Groups List */}
        {!loading && !isInConversation && activeTab === 'groups' && (
          <>
            <button className="chat-create-group-btn" onClick={openCreateGroupModal}>
              + Create Group
            </button>

            <div className="chat-groups-list">
              {groups.length === 0 ? (
                <div className="chat-no-contacts">
                  <p>No groups yet.</p>
                  <p className="chat-hint">Create a group to start chatting with your team!</p>
                </div>
              ) : (
                groups.map((group) => {
                  const groupUnread = unreadPerGroup[group.id] || 0;
                  return (
                    <button
                      key={group.id}
                      className="chat-group-item"
                      onClick={() => selectGroup(group)}
                    >
                      <div className="chat-group-avatar">
                        {group.name?.charAt(0) || '#'}
                        {groupUnread > 0 && (
                          <span className="chat-user-unread-badge">
                            {groupUnread > 99 ? '99+' : groupUnread}
                          </span>
                        )}
                      </div>
                      <div className="chat-group-info">
                        <span className="chat-group-name">{group.name}</span>
                        <span className="chat-group-members-count">
                          {group.member_count || 0} members
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* DM Messages View */}
        {!loading && selectedUser && (
          <>
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-no-messages">
                  <p>No messages yet.</p>
                  <p className="chat-hint">Start the conversation with {selectedUser.name}!</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`chat-message ${msg.is_from_user ? 'outgoing' : 'incoming'}`}
                  >
                    <div className="chat-message-content">{msg.message}</div>
                    <div className="chat-message-meta">
                      <span className="chat-message-date">{formatDate(msg.created_at)}</span>
                      <span className="chat-message-time">{formatTime(msg.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <form className="chat-input-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                className="chat-input"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending}
              />
              <button
                type="submit"
                className="chat-send-btn"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? '...' : '→'}
              </button>
            </form>
          </>
        )}

        {/* Group Messages View */}
        {!loading && selectedGroup && (
          <>
            {/* Room Sidebar Tabs */}
            <div className="room-tab-bar">
              {ROOM_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`room-tab ${roomTab === tab.key ? 'active' : ''}`}
                  onClick={() => setRoomTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Chat View */}
            {roomTab === 'chat' && (
              <>
                <div className="chat-messages">
                  {groupMessages.length === 0 ? (
                    <div className="chat-no-messages">
                      <p>No messages yet.</p>
                      <p className="chat-hint">Start the conversation in {selectedGroup.name}!</p>
                    </div>
                  ) : (
                    groupMessages.map((msg, index) => {
                      const isFromUser = msg.is_from_user || msg.sender_id === currentUser?.id;
                      const senderColorClass = getSenderColorClass(msg.sender_id, groupMembers);
                      const messageParts = parseMentions(msg.message, groupMembers, currentUser?.id);

                      return (
                        <div
                          key={msg.id || index}
                          className={`chat-message group-message ${isFromUser ? 'outgoing' : 'incoming'}`}
                        >
                          {!isFromUser && (
                            <div className={`chat-message-sender ${senderColorClass}`}>
                              {msg.sender_name || msg.sender_username || 'Unknown'}
                            </div>
                          )}
                          <div className="chat-message-content">
                            {messageParts.map((part, i) => (
                              part.type === 'mention' ? (
                                <span key={i} className={`chat-mention ${part.isSelf ? 'mention-self' : ''}`}>
                                  {part.content}
                                </span>
                              ) : (
                                <span key={i}>{part.content}</span>
                              )
                            ))}
                          </div>
                          <div className="chat-message-meta">
                            <span className="chat-message-date">{formatDate(msg.created_at)}</span>
                            <span className="chat-message-time">{formatTime(msg.created_at)}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form className="chat-input-form" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    className="chat-input"
                    placeholder={`Message ${selectedGroup.name}...`}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    className="chat-send-btn"
                    disabled={!newMessage.trim() || sending}
                  >
                    {sending ? '...' : '→'}
                  </button>
                </form>
              </>
            )}

            {/* Module Records View */}
            {roomTab !== 'chat' && (
              <div className="room-records-panel">
                {roomRecordsLoading ? (
                  <div className="chat-loading">Loading...</div>
                ) : roomRecords.length === 0 ? (
                  <div className="room-records-empty">
                    <p>No {ROOM_TABS.find(t => t.key === roomTab)?.label || 'records'} found.</p>
                  </div>
                ) : (
                  <div className="room-records-list">
                    {roomRecords.map((record) => {
                      const attrs = record.attributes || record;
                      const name = attrs.name || attrs.title || attrs.subject ||
                        [attrs.first_name, attrs.last_name].filter(Boolean).join(' ') || 'Untitled';
                      const status = attrs.status || attrs.stage || attrs.quote_stage || attrs.invoice_status || '';
                      const date = attrs.date_modified || attrs.date_entered || '';
                      const id = record.id || attrs.id;

                      return (
                        <div key={id} className="room-record-item">
                          <div className="room-record-name">{name}</div>
                          <div className="room-record-details">
                            {status && <span className="room-record-status">{status}</span>}
                            {date && <span className="room-record-date">{formatDate(date)}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showCreateGroupModal && <CreateGroupModal onClose={closeCreateGroupModal} />}
      {showGroupSettings && selectedGroup && (
        <GroupSettingsModal group={selectedGroup} onClose={closeGroupSettings} />
      )}
    </div>
  );
};

// Floating toggle button with unread badge
export const ChatToggleButton = () => {
  const { isPanelOpen, togglePanel, unreadCount, groupUnreadCount, autoOpenEnabled } = useChatContext();

  // Combined unread count
  const totalUnread = unreadCount + groupUnreadCount;

  // Only show badge when auto-open is disabled and there are unread messages
  const showBadge = !autoOpenEnabled && totalUnread > 0;

  return (
    <button
      className={`chat-toggle-btn ${isPanelOpen ? 'active' : ''}`}
      onClick={togglePanel}
      title={isPanelOpen ? 'Close Chat' : 'Open Chat'}
    >
      {isPanelOpen ? '×' : '💬'}
      {showBadge && (
        <span className="chat-unread-badge">
          {totalUnread > 99 ? '99+' : totalUnread}
        </span>
      )}
    </button>
  );
};

export default ChatPanel;
